import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import type RouterService from '@ember/routing/router-service'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { LessonResourceType, LessonDataField } from 'teacher-ui-client/contentful-client/entry-types'
import type { LessonDataEntry } from 'teacher-ui-client/contentful-client/entry-types'
import { isValidPrecinctForProduct, Precinct } from 'teacher-ui-client/utils/precincts'
import { action } from '@ember/object'
import type { IntlService } from 'ember-intl'
import type InteractivePreviewService from 'teacher-ui-client/services/interactive-preview'
import { ReadingEggsMapQuizPreview, ReadingEggsLessonPreview } from 'teacher-ui-client/interactive-preview/engines'
import { LessonsPage } from 'teacher-ui-client/components/page/lessons'
import { LessonsSearch } from 'teacher-ui-client/components/lessons/search'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector/index'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { PrecinctTabs } from 'teacher-ui-client/components/page-header/precinct-tabs'
import { LessonPreviewTile } from 'teacher-ui-client/components/tiles/lesson/preview'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { eq } from 'ember-truth-helpers'
import { LessonFields } from 'teacher-ui-client/components/lesson/fields'
import { LessonField } from 'teacher-ui-client/components/lesson/field'
import type ReadingEggsLessonsRoute from 'teacher-ui-client/subscription-type/lessons/reading-eggs/previews/route'
import type FeaturesService from 'teacher-ui-client/services/features'

interface Args {
  model: ModelFor<ReadingEggsLessonsRoute>
}

interface Signature {
  Args: Args
}

interface LessonPreviewViewModel {
  id: string
  title: string
  coverUrl?: string
  openPreview?: () => void
  lessonResources: {
    id: string
    title: string
    resourceType: LessonResourceType
    fileUrl?: string
    videoUrl?: string
  }[]
  lessonData: LessonData
}

interface LessonData {
  title: string
  precinct: Precinct
  activityNumber: number
  quiz?: string
  map?: number
  focusSounds?: string[]
  wordsRead?: string[]
  alternatives?: string[]
  wordCount?: number
  phase?: number
  vocabulary?: string[]
  highFrequencySightWords?: string[]
  phonicallyDecodableWords?: string[]
  phonicLettersAndSounds?: string[]
  skills?: string[]
  readingLevel?: string
  focusSoundsWords?: string[]
  challenge?: string[]
  textType?: string[]
  mapTopic?: string
  focusSummary?: string
  focusActivities?: string[]
  [key: string]: unknown
}

export class ReadingEggsPreviewsPage extends Component<Signature> {
  @service router!: RouterService

  @service intl!: IntlService

  @service interactivePreview!: InteractivePreviewService

  @service features!: FeaturesService

  @queryParam('subscription-type/lessons') precinct!: Precinct

  get precinctOrDefault() {
    return isValidPrecinctForProduct(Product.RE, this.precinct) ? this.precinct : this.precincts[0]
  }

  get precincts() {
    return [Precinct.RE_READING, Precinct.RE_STORYLANDS, Precinct.RE_SPELLING]
  }

  @action
  async setPrecinct(precinct: Precinct) {
    await this.router.transitionTo({
      queryParams: {
        precinct,
        pageNumber: 1,
      },
    })
  }

  private readonly resourceOrder: LessonResourceType[] = [
    LessonResourceType.STUDENT_PDF,
    LessonResourceType.TEACHER_PDF,
    LessonResourceType.ES_STUDENT_PDF,
  ]

  get lessonPreviewViewModels(): LessonPreviewViewModel[] {
    if (!this.args.model.loaderInstance?.isSuccessful) return []

    const entries = this.args.model.loaderInstance.value?.lessonsData.items ?? []
    const previews = this.args.model.loaderInstance.value?.previewByLessonDataId ?? {}
    const resources = this.args.model.loaderInstance.value?.resourcesByLessonDataId ?? {}

    return entries.map((entry) => {
      const lessonDataId = entry.sys.id
      const lessonData = entry.fields
      const lessonPreview = previews[lessonDataId]
      const lessonResources = resources[lessonDataId] ?? []

      const lessonDataKeys = Object.values(LessonDataField)
      let title = entry.fields.quiz
        ? this.intl.t('components.tiles.lessonPreviewEntry.quizName', {
            map: entry.fields.map,
          })
        : this.intl.t('components.tiles.lessonPreviewEntry.lessonName', {
            lesson: entry.fields.activityNumber,
            map: entry.fields.map,
          })

      if (this.features.isEnabled('re_cf_lesson_titles')) {
        title = entry.fields.title
      }

      return {
        id: entry.sys.id,
        title,
        coverUrl: lessonPreview?.fields.cover?.fields.file?.url,
        openPreview: this.getOpenPreview(lessonData),
        lessonResources: lessonResources
          .map((resource) => ({
            id: resource.sys.id,
            title: resource.fields.title,
            resourceType: resource.fields.resourceType,
            fileUrl: resource.fields.file?.fields.file?.url,
            videoUrl: resource.fields.videoUrl,
          }))
          .sort((a, b) => this.resourceOrder.indexOf(a.resourceType) - this.resourceOrder.indexOf(b.resourceType)),
        lessonData: {
          title: lessonData.title,
          activityNumber: lessonData.activityNumber,
          precinct: lessonData.precinct,
          map: lessonData.map,
          ...lessonDataKeys.reduce((acc, k: LessonDataField) => {
            return { ...acc, [k]: lessonData[k] }
          }, {}),
        },
      }
    })
  }

  getOpenPreview(lessonData: LessonDataEntry['fields']) {
    return () => {
      const { curriculum } = this.interactivePreview
      if (lessonData.precinct === Precinct.RE_READING || lessonData.precinct === Precinct.RE_SPELLING) {
        this.interactivePreview.openPreview(
          lessonData.quiz
            ? new ReadingEggsMapQuizPreview(this, curriculum, lessonData.precinct, String(lessonData.map))
            : new ReadingEggsLessonPreview(this, curriculum, lessonData.precinct, String(lessonData.activityNumber)),
          { title: lessonData.title },
        )
      }
      if (!lessonData.quiz && lessonData.precinct === Precinct.RE_STORYLANDS) {
        // storylands does not have map quiz
        this.interactivePreview.openPreview(
          new ReadingEggsLessonPreview(this, curriculum, lessonData.precinct, String(lessonData.activityNumber)),
          { title: lessonData.title },
        )
      }
    }
  }

  get title() {
    switch (this.precinct) {
      case Precinct.RE_STORYLANDS:
        return this.intl.t('pages.product.lessons.mapsLessonsTitle', {
          name: this.intl.t('precinct.name.reading-eggs-storylands'),
        })
      case Precinct.RE_SPELLING:
        return this.intl.t('pages.product.lessons.mapsLessonsTitle', {
          name: this.intl.t('precinct.name.reading-eggs-spelling'),
        })
      default:
        return this.intl.t('pages.product.lessons.mapsLessonsTitle', { name: this.intl.t('products.re') })
    }
  }

  <template>
    <LessonsPage @title={{this.title}} @loaderInstance={{@model.loaderInstance}}>
      <:header>
        <PrecinctTabs
          class="mx-auto max-w-md"
          @themeKey={{ThemeKeys.blue}}
          @precincts={{this.precincts}}
          @precinct={{this.precinctOrDefault}}
          @changeTab={{this.setPrecinct}}
        />
        <LessonsSearch @themeKey={{ThemeKeys.blue}} />
      </:header>
      <:results>
        {{#each this.lessonPreviewViewModels as |viewModel|}}
          <LessonPreviewTile
            data-test-lesson-preview-entry={{viewModel.lessonData.activityNumber}}
            class="w-80"
            @entryId={{viewModel.id}}
            @title={{viewModel.title}}
            @coverUrl={{viewModel.coverUrl}}
            @coverClass="h-40"
            @lessonResources={{viewModel.lessonResources}}
            @openPreview={{viewModel.openPreview}}
          >
            {{#unless viewModel.lessonData.quiz}}
              {{#if (eq viewModel.lessonData.precinct Precinct.RE_READING)}}
                <ReadingEggsPreviewsReading @lessonDataFields={{viewModel.lessonData}} />
              {{else if (eq viewModel.lessonData.precinct Precinct.RE_SPELLING)}}
                <ReadingEggsPreviewsSpelling @lessonDataFields={{viewModel.lessonData}} />
              {{else if (eq viewModel.lessonData.precinct Precinct.RE_STORYLANDS)}}
                <ReadingEggsPreviewsStorylands @lessonDataFields={{viewModel.lessonData}} />
              {{/if}}
            {{/unless}}
          </LessonPreviewTile>
        {{else}}
          <NoResultsMessage data-test-empty />
        {{/each}}
        {{#if @model.loaderInstance.value.pageCount}}
          <BasePageSelector
            class="basis-full"
            @pageCount={{@model.loaderInstance.value.pageCount}}
            @lockPerPage={{true}}
            @itemsPerPage={{11}}
            @itemCount={{@model.loaderInstance.value.lessonsData.total}}
          />
        {{/if}}
      </:results>
    </LessonsPage>
  </template>
}

interface ReadingSignature {
  Args: {
    lessonDataFields: {
      phonicallyDecodableWords?: string[]
      vocabulary?: string[]
      phonicLettersAndSounds?: string[]
      highFrequencySightWords?: string[]
      [key: string]: unknown
    }
  }
}

export const ReadingEggsPreviewsReading: TOC<ReadingSignature> = <template>
  <LessonFields>
    <LessonField @key="phonicallyDecodableWords" @fields={{@lessonDataFields}} />
    <LessonField @key="vocabulary" @fields={{@lessonDataFields}} />
    <LessonField @key="phonicLettersAndSounds" @fields={{@lessonDataFields}} />
    <LessonField @key="highFrequencySightWords" @fields={{@lessonDataFields}} />
  </LessonFields>
</template>

interface SpellingSignature {
  Args: {
    lessonDataFields: {
      focusSoundWords?: string[]
      challenge?: string[]
      highFrequencySightWords?: string[]
      [key: string]: unknown
    }
  }
}

export const ReadingEggsPreviewsSpelling: TOC<SpellingSignature> = <template>
  <LessonFields>
    <LessonField @key="focusSoundWords" @fields={{@lessonDataFields}} />
    <LessonField @key="highFrequencySightWords" @fields={{@lessonDataFields}} />
    <LessonField @key="challenge" @fields={{@lessonDataFields}} />
  </LessonFields>
</template>

interface StorylandsSignature {
  Args: {
    lessonDataFields: {
      title: string
      quiz?: string
      wordCount?: number
      vocabulary?: string[]
      readingLevel?: string
      highFrequencySightWords?: string[]
      skills?: string[]
      [key: string]: unknown
    }
  }
}

export const ReadingEggsPreviewsStorylands: TOC<StorylandsSignature> = <template>
  <LessonFields>
    <LessonField @key="vocabulary" @fields={{@lessonDataFields}} />
    <LessonField @key="title" @fields={{@lessonDataFields}} />
    <LessonField @key="highFrequencySightWords" @fields={{@lessonDataFields}} />
    <LessonField @key="wordCount" @fields={{@lessonDataFields}} />
    <LessonField @key="readingLevel" @fields={{@lessonDataFields}} />
    <LessonField @key="skills" @fields={{@lessonDataFields}} />
  </LessonFields>
</template>

export default RouteTemplate(ReadingEggsPreviewsPage)
